import { useState } from 'react';
import { Button, FileInput, Group, Modal, Select, Stack, TextInput, Textarea } from '@mantine/core';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';

const documentTypes = ['text', 'url', 'file'] as const;

type DocumentType = (typeof documentTypes)[number];

const documentTypeOptions = [
  { value: 'text', label: 'Text Content' },
  { value: 'url', label: 'URL' },
  { value: 'file', label: 'File Upload' },
];

function isDocumentType(value: string): value is DocumentType {
  return documentTypes.some((t) => t === value);
}

type AddDocumentModalProps = {
  personaId: string;
  opened: boolean;
  onClose: () => void;
};

export function AddDocumentModal({ personaId, opened, onClose }: AddDocumentModalProps) {
  const [type, setType] = useState<DocumentType>('text');
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setName('');
    setContent('');
    setUrl('');
    setFile(null);
    setError(null);
    onClose();
  };

  const submit = async () => {
    const documents = api.personas({ personaId }).documents;

    if (type === 'text') return await documents.text.post({ name: name.trim(), content });
    if (type === 'url') return await documents.url.post({ name: name.trim(), url });

    return file ? await documents.file.post({ name: name.trim(), file }) : null;
  };

  const handleAdd = async () => {
    setIsSubmitting(true);
    setError(null);

    const result = await submit();

    setIsSubmitting(false);

    if (result?.error) {
      setError(getErrorMessage(result.error, 'Failed to add document'));

      return;
    }

    close();
  };

  const isIncomplete =
    !name.trim() || (type === 'text' ? !content.trim() : type === 'url' ? !url.trim() : file === null);

  return (
    <Modal opened={opened} onClose={close} title="Add Document">
      <Stack>
        <ErrorAlert message={error} />
        <TextInput
          label="Name"
          placeholder="e.g., FAQ Document"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <Select
          label="Type"
          value={type}
          onChange={(value) => value && isDocumentType(value) && setType(value)}
          data={documentTypeOptions}
        />
        <DocumentSourceInput
          type={type}
          content={content}
          url={url}
          file={file}
          onContentChange={setContent}
          onUrlChange={setUrl}
          onFileChange={setFile}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={close}>
            Cancel
          </Button>
          <Button onClick={handleAdd} loading={isSubmitting} disabled={isIncomplete}>
            Add
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

type DocumentSourceInputProps = {
  type: DocumentType;
  content: string;
  url: string;
  file: File | null;
  onContentChange: (content: string) => void;
  onUrlChange: (url: string) => void;
  onFileChange: (file: File | null) => void;
};

function DocumentSourceInput({
  type,
  content,
  url,
  file,
  onContentChange,
  onUrlChange,
  onFileChange,
}: DocumentSourceInputProps) {
  if (type === 'text') {
    return (
      <Textarea
        label="Content"
        placeholder="Paste your content here..."
        value={content}
        onChange={(e) => onContentChange(e.target.value)}
        minRows={6}
        autosize
        required
      />
    );
  }

  if (type === 'url') {
    return (
      <TextInput
        label="URL"
        placeholder="https://example.com/document"
        value={url}
        onChange={(e) => onUrlChange(e.target.value)}
        required
      />
    );
  }

  return <FileInput label="File" placeholder="Choose a file" value={file} onChange={onFileChange} clearable required />;
}
